import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

// Chave de localStorage pra lembrar se o usuário recolheu o letreiro — pedido do Robson
// (24/09/2026): "incluir um botão para recolher e expandir o letreiro". Guardado por
// navegador/aparelho (não por conta), igual à maioria das preferências só-de-tela do app.
const COLLAPSE_KEY = "iapauta_ticker_collapsed";

// Lote 146 (Robson, 30/10/2026): todo ALERTA e PENDÊNCIA do letreiro (mensagem com `ciente: true`) tem um botão "Ciente" que o elimina; as informações comuns não têm. A chave é o próprio
// texto da frase: se o conteúdo mudar (outra contagem, outra data), ela volta como aviso novo. Guardado só neste
// aparelho, no mesmo padrão do "Ciente" de conflitos e prazos do Painel; as 300 últimas ficam.
const CIENTE_KEY = "iapauta_letreiro_ciente";
const CIENTE_MAX = 300;
const lerCientes = () => {
  try { const a = JSON.parse(localStorage.getItem(CIENTE_KEY) || "[]"); return Array.isArray(a) ? a.filter((k) => typeof k === "string") : []; } catch { return []; }
};
const gravarCientes = (lista) => { try { localStorage.setItem(CIENTE_KEY, JSON.stringify(lista.slice(-CIENTE_MAX))); } catch { /* sem storage */ } };

// Faixa de mensagens curtas que alternam sozinhas no Painel (status da sincronização,
// resumo da semana, avisos de validação pendente). Cada mensagem entra "digitada" letra
// por letra (como se o app estivesse escrevendo), fica parada um tempo pra leitura, e só
// então some pra dar lugar à próxima — pedido do Robson (21/09/2026), trocando o fade puro
// de antes por esse efeito e alargando a caixa (antes era 1 linha só, cortando com "..."
// frases mais longas).
//
// Pra reverter pro comportamento anterior (fade simples, 1 linha, sem digitação): trocar
// TYPEWRITER_ENABLED pra false abaixo — o resto do componente volta sozinho ao modo antigo
// (larguras antigas incluídas), sem precisar desfazer mais nada.
const TYPEWRITER_ENABLED = true;
// Lote 96 (Robson, 22/10/2026): "está muito rápido" — a digitação ficou mais lenta e a pausa depois
// de terminar a frase passou a ser de 4 s. Na 1ª e 2ª abertura do Painel no dia ("fast") o ritmo é
// um pouco mais ágil (pausa de 2 s); a partir da 3ª abertura vale o ritmo normal.
// Lote 132 (Robson, 27/10/2026): "muito rápido na primeira vez que passa" — ritmo padronizado, igual em toda
// abertura: 3 s de pausa DEPOIS da frase completa; aviso urgente ("vence hoje", revisão periódica) fica 5 s.
// Lote 137 (Robson, 28/10/2026): "atrasar um pouco mais" e "ao final dar o tempo necessário para ler, com calma" —
// digitação a 85 ms por letra e pausa PROPORCIONAL ao tamanho da frase, depois dela completa: 1,5 s + 0,45 s por
// palavra, no mínimo 4 s (frase de 12 palavras ≈ 7 s). Vale para toda abertura (sem modo "rápido").
const CHAR_DELAY_MS = 85;
const CHAR_DELAY_FAST_MS = 85;
const READ_PAUSE_MS = 4000;       // mínimo da pausa de leitura
const READ_PAUSE_FAST_MS = 4000;
const READ_BASE_MS = 1500;
const READ_PER_WORD_MS = 450;
const URGENT_MS = 5000;
export const readPauseFor = (text, minMs = READ_PAUSE_MS) => {
  const palavras = String(text || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(minMs, READ_BASE_MS + READ_PER_WORD_MS * palavras);
};
// Lote 79 — duas fases: (1) "completa": passa por TODAS as mensagens (inclusive dica de uso)
// FULL_RUNS vezes; (2) "ações": depois só alterna as principais ações, ~5 s cada, sem parar.
// Uma mensagem pode ser texto simples ou { text, to } — com `to`, tocar nela abre aquela tela.
const FULL_RUNS = 2;
const msgText = (m) => (typeof m === "string" ? m : m?.text || "");
const msgCiente = (m) => typeof m === "object" && !!m?.ciente;   // Lote 146: só alertas e pendências têm o botão "Ciente"
const msgTone = (m) => (typeof m === "object" ? m?.tone : null);   // Lote 137: { tone: "red" } = aviso vermelho do letreiro
const prefersReducedMotion = () => {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
};

// Lote 102 (Robson, 23/10/2026): o antigo card "Ação agora" do Painel virou uma mensagem VERMELHA
// do letreiro. `urgent` = { key, text, to, buttons:[{ label, primary, onClick | to }] }. Ela abre o
// Painel (aparece primeiro), volta a cada rodada enquanto não for resolvida e continua visível mesmo com o
// letreiro recolhido. Para reverter ao comportamento anterior, basta não passar `urgent`.
// Lote 132: `urgents` = lista de avisos (vermelho = prazo/audiência/conflito; azul = revisão periódica e
// movimentação de processo prioritário). Com vários, eles se INTERCALAM com as frases do letreiro (um aviso
// depois de cada frase) e, sem frases ou com o letreiro recolhido, se alternam sozinhos a cada 5 s.
const TOM = {
  red: { box: "bg-red-50 border-red-300 text-red-800", dot: "bg-red-600", primary: "bg-red-700 border-red-700 text-white hover:bg-red-800", sec: "bg-white/70 border-red-200 text-red-700 hover:bg-red-100" },
  blue: { box: "bg-blue-50 border-blue-300 text-blue-800", dot: "bg-blue-600", primary: "bg-blue-700 border-blue-700 text-white hover:bg-blue-800", sec: "bg-white/70 border-blue-200 text-blue-700 hover:bg-blue-100" },
};

export default function InfoTicker({ messages: messagesProp, actionMessages: actionProp, urgent = null, urgents = null, startFull = true, intervalMs = 3000, fast = false }) {
  // Lote 146: avisos já marcados "Ciente" saem das duas listas (o resto do componente só enxerga as filtradas).
  const [cientes, setCientes] = useState(lerCientes);
  const messages = useMemo(() => (Array.isArray(messagesProp) ? messagesProp.filter((m) => !(msgCiente(m) && cientes.includes(msgText(m)))) : messagesProp), [messagesProp, cientes]);
  const actionMessages = useMemo(() => (Array.isArray(actionProp) ? actionProp.filter((m) => !(msgCiente(m) && cientes.includes(msgText(m)))) : actionProp), [actionProp, cientes]);
  const charDelay = fast ? CHAR_DELAY_FAST_MS : CHAR_DELAY_MS;
  const readPause = fast ? READ_PAUSE_FAST_MS : READ_PAUSE_MS;
  const navigate = useNavigate();
  const hasActions = Array.isArray(actionMessages) && actionMessages.length > 0;
  // Sem mensagens de ação, ou quando a fase completa não deve repetir (já viu hoje, sem
  // novidade), vai direto pra fase 2 — se ela existir.
  const [phase, setPhase] = useState(startFull || !hasActions ? "full" : "actions");
  const [round, setRound] = useState(0);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [typedLength, setTypedLength] = useState(0);
  const [paused, setPaused] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const urgentList = Array.isArray(urgents) && urgents.length ? urgents : (urgent ? [urgent] : []);
  const urgentKey = urgentList.length ? urgentList.map((u) => u.key).join("¦") : null;
  const [urgentShowing, setUrgentShowing] = useState(urgentList.length > 0);
  const [urgentIdx, setUrgentIdx] = useState(0);
  const curUrgent = urgentList.length ? urgentList[urgentIdx % urgentList.length] : null;
  const timersRef = useRef([]);
  const list = phase === "actions" && hasActions ? actionMessages : messages;

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        // localStorage indisponível (aba anônima, etc.) — só não lembra da próxima vez
      }
      return next;
    });
  };

  const cienteAtual = () => {
    if (!list || list.length === 0) return;
    if (!msgCiente(list[index % list.length])) return;
    const t = msgText(list[index % list.length]);
    if (!t) return;
    const antes = cientes;
    const depois = [...antes.filter((k) => k !== t), t].slice(-CIENTE_MAX);
    setCientes(depois); gravarCientes(depois);
    setPaused(false); setTypedLength(0); setVisible(true);
    toast("Aviso dispensado", { duration: 6000, action: { label: "Desfazer", onClick: () => { setCientes(antes); gravarCientes(antes); } } });
  };

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  // Mensagens novas chegando (ex.: dados do Painel carregaram) — reinicia do começo.
  useEffect(() => {
    setIndex(0);
    setRound(0);
    setVisible(true);
    setTypedLength(0);
    setPhase(startFull || !hasActions ? "full" : "actions");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesProp?.length, actionProp?.length, startFull]);

  // Urgente novo (ou o primeiro): entra na frente de tudo.
  useEffect(() => {
    setUrgentShowing(!!urgentKey);
    setUrgentIdx(0);
  }, [urgentKey]);

  // Próxima mensagem; no fim da lista completa conta uma volta e, completadas FULL_RUNS,
  // troca pra fase de ações.
  const advance = () => {
    setTypedLength(0);   // Lote 137: a próxima frase começa vazia (antes ela aparecia inteira por um instante e só então era digitada)
    const len = list.length;
    if (index + 1 < len) {
      setIndex(index + 1);
      if (urgentList.length > 1) setUrgentShowing(true);   // Lote 132: vários avisos se intercalam com as frases
      return;
    }
    if (urgentKey) setUrgentShowing(true);   // fim da rodada: o aviso volta enquanto não for resolvido
    if (phase === "full") {
      if (hasActions && round + 1 >= FULL_RUNS) { setPhase("actions"); setRound(0); setIndex(0); return; }
      setRound((r) => r + 1);
    }
    setIndex(0);
  };

  useEffect(() => {
    clearTimers();
    if (!list || list.length === 0) return undefined;
    if (urgentKey && urgentShowing) return clearTimers;   // vermelho na tela: o letreiro normal espera
    const current = msgText(list[index % list.length]);

    if (paused) { setTypedLength(current.length); setVisible(true); return undefined; }

    const reduce = prefersReducedMotion();
    if (!TYPEWRITER_ENABLED || reduce) {
      setTypedLength(current.length);
      setVisible(true);
      if (list.length <= 1 && phase === "actions") return undefined;
      if (list.length <= 1 && !hasActions) return undefined;
      timersRef.current.push(
        setTimeout(() => {
          setVisible(false);
          timersRef.current.push(setTimeout(() => { advance(); setVisible(true); }, 250));
        }, Math.max(readPauseFor(current, readPause), reduce ? 4000 : 0)),
      );
      return clearTimers;
    }

    // Efeito de digitação: revela um caractere por vez; ao terminar, espera com o texto
    // completo (na fase de ações o total por mensagem fica perto de 5 s) e passa pra próxima.
    setTypedLength(0);
    setVisible(true);
    const pause = readPauseFor(current, readPause);   // Lote 137: tempo de leitura proporcional à frase, DEPOIS dela completa
    let i = 0;
    const typeNext = () => {
      i += 1;
      setTypedLength(i);
      if (i < current.length) {
        timersRef.current.push(setTimeout(typeNext, charDelay));
      } else if (list.length > 1 || (phase === "full" && hasActions) || urgentList.length > 0) {   // Lote 132: com aviso pendente, o ciclo continua mesmo com 1 só frase
        timersRef.current.push(setTimeout(advance, pause));
      }
    };
    timersRef.current.push(setTimeout(typeNext, charDelay));
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, actionMessages, index, phase, round, paused, fast, urgentKey, urgentShowing]);

  const listEmpty = !list || list.length === 0;

  // Cada aviso fica 5 s; parado enquanto o dedo/mouse estiver em cima. Sem frases no letreiro (ou recolhido)
  // o aviso é permanente — e, com vários, eles se alternam a cada 5 s.
  useEffect(() => {
    if (!urgentKey || !urgentShowing || paused) return undefined;
    if (collapsed || listEmpty) {
      if (urgentList.length < 2) return undefined;
      const t = setTimeout(() => setUrgentIdx((i) => (i + 1) % urgentList.length), URGENT_MS);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => { setUrgentShowing(false); setUrgentIdx((i) => (i + 1) % Math.max(urgentList.length, 1)); }, URGENT_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urgentKey, urgentShowing, urgentIdx, paused, collapsed, listEmpty]);

  const tom = TOM[curUrgent?.tone] || TOM.red;
  const urgentBox = curUrgent ? (
    <div
      data-testid="info-ticker-urgent"
      data-urgent-key={curUrgent.key}
      data-tone={curUrgent.tone || "red"}
      data-total={urgentList.length}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onPointerDown={() => setPaused(true)}
      className={`flex flex-col sm:flex-row sm:items-end gap-x-3 gap-y-1.5 border rounded-2xl pl-3 pr-2 py-2 text-sm w-full sm:w-fit max-w-full sm:max-w-lg md:max-w-2xl overflow-hidden ${tom.box}`}
    >
      <span className="flex items-start gap-2 flex-1 min-w-0">
        <span className={`w-2 h-2 rounded-full shrink-0 mt-1.5 motion-safe:animate-pulse ${tom.dot}`} style={{ animationDuration: "1s" }} />
        <span
          role={curUrgent.to ? "link" : undefined}
          tabIndex={curUrgent.to ? 0 : undefined}
          data-testid="info-ticker-urgent-text"
          onClick={curUrgent.to ? () => navigate(curUrgent.to) : undefined}
          onKeyDown={curUrgent.to ? (e) => { if (e.key === "Enter") navigate(curUrgent.to); } : undefined}
          className={`font-medium leading-snug min-w-0 break-words [overflow-wrap:anywhere] line-clamp-3 ${curUrgent.to ? "cursor-pointer underline-offset-2 hover:underline" : ""}`}
        >
          {curUrgent.text}
        </span>
      </span>
      <span className="flex items-center gap-1.5 shrink-0 self-end">
        {(curUrgent.buttons || []).map((b) => (
          <button
            key={b.label}
            type="button"
            data-testid={`info-ticker-urgent-btn-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => { if (b.onClick) b.onClick(); else if (b.to) navigate(b.to); }}
            className={`relative h-8 px-3 rounded-lg text-xs font-semibold border after:absolute after:-inset-1 after:content-[""] ${
              b.primary ? tom.primary : tom.sec
            }`}
          >
            {b.label}
          </button>
        ))}
      </span>
    </div>
  ) : null;

  if (listEmpty) return urgentBox;

  const currentMsg = list[index % list.length];
  const current = msgText(currentMsg);
  const target = typeof currentMsg === "object" ? currentMsg?.to : null;
  const onMsgClick = typeof currentMsg === "object" ? currentMsg?.onClick : null;
  const isRed = msgTone(currentMsg) === "red";
  const acionavel = !!(target || onMsgClick);
  const acionar = () => { if (onMsgClick) onMsgClick(); else if (target) navigate(target); };
  const shown = TYPEWRITER_ENABLED ? current.slice(0, typedLength) : current;
  const stillTyping = TYPEWRITER_ENABLED && typedLength < current.length;

  if (collapsed) {
    return (
      <div className="flex flex-wrap items-start gap-2">
      {urgentBox}
      <button
        type="button"
        data-testid="info-ticker-expand"
        onClick={toggleCollapsed}
        title="Mostrar o painel de informações"
        className="inline-flex items-center gap-2 bg-gold-500/15 border border-gold-500/25 rounded-2xl pl-3 pr-2.5 py-2 text-sm text-gold-700"
      >
        <span
          className="w-2 h-2 rounded-full bg-emerald-brand shrink-0 animate-pulse"
          style={{ animationDuration: "1s" }}
        />
        <ChevronDown className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
      </div>
    );
  }

  if (urgentBox && urgentShowing) return urgentBox;

  return (
    <div
      data-testid="info-ticker"
      data-phase={phase}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onPointerDown={() => setPaused(true)}
      data-tone={isRed ? "red" : "normal"}
      className={`inline-flex items-start gap-2 border rounded-2xl pl-3 pr-2.5 py-2 text-sm max-w-full ${
        isRed ? "bg-red-50 border-red-300 text-red-800" : "bg-gold-500/15 border-gold-500/25 text-gold-700"
      } ${TYPEWRITER_ENABLED ? "sm:max-w-md md:max-w-xl" : ""}`}
    >
      <span
        className={`w-2 h-2 rounded-full shrink-0 animate-pulse mt-1.5 ${isRed ? "bg-red-600" : "bg-emerald-brand"}`}
        style={{ animationDuration: "1s" }}
      />
      <span
        role={acionavel ? "link" : undefined}
        tabIndex={acionavel ? 0 : undefined}
        data-testid="info-ticker-text"
        onClick={acionavel ? acionar : undefined}
        onKeyDown={acionavel ? (e) => { if (e.key === "Enter") acionar(); } : undefined}
        className={`flex-1 min-w-0 break-words transition-opacity duration-300 leading-snug ${visible ? "opacity-100" : "opacity-0"} ${
          TYPEWRITER_ENABLED ? "" : "truncate"
        } ${acionavel ? "cursor-pointer underline-offset-2 hover:underline" : ""}`}
      >
        {shown}
        {stillTyping && (
          <span className="inline-block w-[2px] h-[1em] align-middle bg-gold-500 ml-0.5 animate-pulse" />
        )}
      </span>
      {msgCiente(currentMsg) && (
        <button
          type="button"
          data-testid="info-ticker-ciente"
          onClick={cienteAtual}
          title="Ciente — dispensar este aviso"
          className={`relative shrink-0 h-6 px-2.5 rounded-full border text-[11px] font-semibold after:absolute after:-inset-1.5 after:content-[""] ${
            isRed ? "border-red-300 bg-white/70 text-red-700 hover:bg-red-100" : "border-gold-500/40 bg-white/60 text-gold-800 hover:bg-white"
          }`}
        >
          Ciente
        </button>
      )}
      <button
        type="button"
        data-testid="info-ticker-collapse"
        onClick={toggleCollapsed}
        title="Recolher o painel de informações"
        className={`shrink-0 mt-0.5 ${isRed ? "text-red-700/70 hover:text-red-800" : "text-gold-700/70 hover:text-gold-700"}`}
      >
        <ChevronUp className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
    </div>
  );
}
